/**
 * Duplicates merge-review surface (#187) — RO-2-03 conversion of the
 * `mergeInto` server action (`excellent/src/database/duplicates-actions.ts`).
 *
 * The client sends a winner/loser URN pair; the route delegates to the
 * duplicates-data adapter (`excellent/src/database/duplicates-data.ts`), which
 * wraps core's `mergeEntities` — the same merge authority the `entity_merge`
 * MCP tool calls.
 */

import { z } from "zod";
import type { Operation } from "../../types";

export const duplicatesMergeBody = z.object({
  /** The "loser" — its rows are re-pointed to `toUrn` and it is soft-deleted. */
  fromUrn: z.string().min(1),
  /** The "winner" — survives with the merged data. */
  toUrn: z.string().min(1),
});
export type DuplicatesMergeBody = z.infer<typeof duplicatesMergeBody>;

export const duplicatesMergeResponse = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
});
export type DuplicatesMergeResponse = z.infer<typeof duplicatesMergeResponse>;

// ── RO-3-11 (#44304) — the read the merge-review page needs ─────────────────
//
// `/database/duplicates` was a server component: it called
// `listDuplicateCandidates(workspaceId)` and then resolved BOTH sides of every
// pair to a display summary in the same render ("server-side, no round-trips",
// says the original's comment). The port keeps that join server-side — one
// request returns the pairs already resolved — rather than N+1 URN lookups from
// the browser, which is the shape the comment was defending.

/** One side of a candidate pair, resolved for display (`EntitySummary`). */
export const duplicateEntitySummarySchema = z.object({
  urn: z.string(),
  /** Kind slug, e.g. "person" | "organization". */
  kind: z.string(),
  /** Display name / title. */
  name: z.string(),
  /** ISO-8601 created_at from the row, or null. */
  createdAt: z.string().nullable(),
});
export type DuplicateEntitySummaryDto = z.infer<typeof duplicateEntitySummarySchema>;

/** A candidate pair as the detector surfaced it (`DuplicateCandidate`). */
export const duplicateCandidateSchema = z.object({
  a_urn: z.string(),
  b_urn: z.string(),
  reason: z.string(),
  score: z.number(),
});
export type DuplicateCandidateDto = z.infer<typeof duplicateCandidateSchema>;

export const duplicatePairSchema = z.object({
  candidate: duplicateCandidateSchema,
  a: duplicateEntitySummarySchema,
  b: duplicateEntitySummarySchema,
});
export type DuplicatePairDto = z.infer<typeof duplicatePairSchema>;

export const duplicatesListResponse = z.object({
  pairs: z.array(duplicatePairSchema),
  /**
   * Whether the #185 detector is still stubbed. Carried in the RESPONSE rather
   * than read from a client constant because `STUB_ACTIVE` lives in
   * `excellent/src/database/duplicates-data.ts`, a server module — the surface's
   * "detector: stub" notice has to be told, not import it.
   */
  stubActive: z.boolean(),
});
export type DuplicatesListResponse = z.infer<typeof duplicatesListResponse>;

/**
 * READ — the open duplicate-candidate queue for the active workspace, each pair
 * resolved to display summaries. Workspace-scoped server-side (same discipline
 * as the conflicts queue): no workspace → no candidates, never a widened
 * all-workspaces read.
 */
export const duplicatesList: Operation<
  void,
  void,
  void,
  z.infer<typeof duplicatesListResponse>
> = {
  method: "GET",
  path: () => "/api/database/duplicates",
  response: duplicatesListResponse,
};

// Merge is declared AFTER list so `invalidates` can name the read by value
// (RO-5-02b / #44476). Original: `revalidatePath(DUPLICATES_PATH)`.
export const duplicatesMerge: Operation<
  void,
  void,
  z.infer<typeof duplicatesMergeBody>,
  z.infer<typeof duplicatesMergeResponse>
> = {
  method: "POST",
  path: () => "/api/database/duplicates/merge",
  body: duplicatesMergeBody,
  response: duplicatesMergeResponse,
  // Port of `revalidatePath("/database/duplicates")` — a successful merge
  // removes the pair from the candidate queue the list serves.
  invalidates: [duplicatesList],
};

export const duplicates = {
  merge: duplicatesMerge,
  list: duplicatesList,
};
